import {
  createEntityAdapter,
  createSelector,
  createSlice,
  Dictionary,
  EntityId,
} from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import { DEPARTMENT_NOT_SET } from 'features/scheduleBuilder/ScheduleBuilderView/constants';
import RoleLaborTotals from 'features/scheduleBuilder/ScheduleBuilderView/resources/RoleLaborTotals';
import ScheduleBuilderSession from 'features/scheduleBuilder/ScheduleBuilderView/resources/ScheduleBuilderSession';
import {
  selectCurrentDateRangeEnd,
  selectCurrentDateRangeStart,
  selectJobIdUserIdMap,
} from 'features/scheduleBuilder/ScheduleBuilderView/selectors/shared';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';
import { selectRoleEntities, selectRoleIdRoleNameMap } from '../roles/slice';
import { Role } from '../roles/types';
import { SessionFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import {
  Department,
  DepartmentSerializedEntity,
  DepartmentsState,
  Role as DeptRole,
} from './types';

export const departmentsAdapter = createEntityAdapter<Department>();

export const initialState: DepartmentsState = {
  ...departmentsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      ScheduleBuilderSession.resourceActionEvent('fetch'),
      (state: any, action: SessionFetchAction) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const departments = action?.payload?.included
              .filter(
                (entity: DepartmentSerializedEntity) =>
                  entity.type === 'department'
              )
              .map((entity: DepartmentSerializedEntity) => ({
                id: entity.id,
                type: 'department' as const,
                name: entity.attributes.name,
                roles: entity.relationships?.roles?.data as DeptRole[],
                ...Object.fromEntries(
                  Object.entries(entity.relationships || {}).map(
                    ([k, v]: [string, any]) => [k, v.data]
                  )
                ),
              }));
            departmentsAdapter.setAll(state, departments);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const { reducer, actions } = slice;

export const {
  selectById: selectDepartmentById,
  selectIds: selectDepartmentIds,
  selectEntities: selectDepartmentEntities,
  selectAll: selectAllDepartments,
  selectTotal: selectTotalDepartments,
} = departmentsAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const selectDepartmentsLoadingStatus = (state: RootState): boolean =>
  getSliceState(state)?.[SLICE_NAME]?.status === 'pending';

export const selectDepartmentAttribute = <T extends keyof Department>({
  state,
  id,
  attribute,
}: {
  state: RootState;
  id: EntityId;
  attribute: T;
}): Department[T] | undefined =>
  selectAttributeForEntity({
    state,
    attribute,
    entitySelector: selectDepartmentById,
    id,
  });

export const selectDepartmentsWhereAttribute = createCachedSelector(
  [
    selectAllDepartments,
    (
      _: RootState,
      {
        attribute,
        value,
      }: { attribute: keyof Department; value: Department[keyof Department] }
    ) => ({ attribute, value }),
  ],
  (departments, { attribute, value }) =>
    departments.filter(dept => dept && dept[attribute] === value)
)(
  (
    _,
    params?: {
      attribute: keyof Department;
      value: Department[keyof Department];
    }
  ) => `selectDepartmentsWhereAttribute-${params?.attribute}-${params?.value}`
);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const selectSortedDepartments = createSelector(
  [selectAllDepartments],
  departments => {
    const sortedDepartments = [...departments].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    return sortedDepartments;
  }
);

export const selectSortedDepartmentIds = createSelector(
  [selectSortedDepartments],
  departments => departments.map((dept: Department) => dept.id)
);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const selectSortDepartmentByName = createSelector(
  [selectAllDepartments],
  departments => {
    const notSet = departments.find(d => d.name === DEPARTMENT_NOT_SET);

    const sorted = [...departments]
      .filter(d => d.name !== DEPARTMENT_NOT_SET)
      .sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

    return notSet ? [...sorted, notSet] : sorted;
  }
);

export const selectDepartmentOptions = createSelector(
  [selectSortDepartmentByName],
  departments =>
    departments.map(dept => ({
      value: dept.id,
      label: dept.name,
    }))
);

export const selectRoleOptionsWithDepartment = createSelector(
  selectSortDepartmentByName,
  selectRoleIdRoleNameMap,
  (departments, roleIdRoleNameMap) => {
    const roleOptions: any[] = [];
    departments.map(department => {
      roleOptions.push({
        groupLabel: department.name,
      });
      department.roles.map((role: DeptRole) => {
        if (roleIdRoleNameMap[Number(role.id)]) {
          roleOptions.push({
            value: role.id,
            label: roleIdRoleNameMap[Number(role.id)],
          });
        }
        return roleOptions;
      });
      return roleOptions;
    });
    return roleOptions;
  }
);

export const selectDepartmentNotSetId = createSelector(
  [selectAllDepartments],
  departments => {
    const departmentNotSet = departments.find(
      dept => dept.name === DEPARTMENT_NOT_SET
    );
    return departmentNotSet?.id;
  }
);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const selectSortedRolesByDepartmentId = createSelector(
  (state: RootState) => state,
  (_: RootState, props: { departmentId: EntityId }) => props.departmentId,
  selectRoleEntities,
  (state, departmentId, rolesMap: Dictionary<Role>): EntityId[] => {
    const department = selectDepartmentById(state, departmentId);

    if (!department || !department.roles) {
      return [];
    }

    const roleIds = Array.isArray(department.roles)
      ? department.roles.map(roleOrId =>
          typeof roleOrId === 'object' ? roleOrId.id : roleOrId
        )
      : [];

    /** The backend returns a list of roleIds that have already
     * been deleted in department relationships, we filter them out here
     */

    const validRolesIds = roleIds.filter(id => rolesMap[id]);
    const allValidRoles: Role[] = validRolesIds
      .map(role => rolesMap[role])
      .filter(Boolean) as Role[];
    const sortedRoleIds: EntityId[] = allValidRoles
      .sort((a, b) => {
        if (!a.name && !b.name) return 0;
        if (!a.name) return 1;
        if (!b.name) return -1;
        return a.name.localeCompare(b.name);
      })
      .map(role => role?.id);

    return sortedRoleIds;
  }
);

export const selectRoleLaborTotalsByDepartmentByDate = createCachedSelector(
  RoleLaborTotals.selectAllEntities,
  (state: RootState) => selectJobIdUserIdMap(state),
  (state: RootState, props: { departmentId: EntityId; date?: string }) =>
    selectDepartmentById(state, props.departmentId),
  (state: RootState, props: { departmentId: EntityId; date?: string }) =>
    props.date || selectCurrentDateRangeStart(state),
  (state: RootState, props: { departmentId: EntityId; date?: string }) =>
    props.date || selectCurrentDateRangeEnd(state),
  (roleLaborTotals, jobIdUserIdMap, department, startRange, endRange) => {
    startRange = new Date(startRange);
    endRange = new Date(endRange);

    const roleIds = department?.roles
      ? Object.fromEntries(department.roles.map(role => [role.id, true]))
      : {};

    const filtered = roleLaborTotals.filter(roleLaborTotal => {
      const totalsDate = new Date(roleLaborTotal.attributes.date);
      const inRange = totalsDate >= startRange && totalsDate <= endRange;
      const departmentHasRole =
        roleIds[roleLaborTotal.relationships.role.data?.id];
      const isDeptNotSet =
        !roleLaborTotal.relationships.role.data &&
        department?.name === DEPARTMENT_NOT_SET;

      return (
        inRange &&
        (departmentHasRole || isDeptNotSet) &&
        Object.keys(jobIdUserIdMap).includes(
          roleLaborTotal?.relationships?.job?.data?.id
        )
      );
    });

    return filtered;
  }
)(
  (_, props) =>
    `selectRoleLaborTotalsByDepartmentByDate-${props.date}-${props.departmentId}`
);

export const selectDepartmentHoursByDate = createCachedSelector(
  (state: RootState, props: { departmentId: EntityId; date?: string }) =>
    selectRoleLaborTotalsByDepartmentByDate(state, props),
  roleLaborTotals =>
    roleLaborTotals.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.attributes.hours || 0),
      0
    )
)(
  (_, props) =>
    `selectDepartmentHoursByDate-${props.date}-${props.departmentId}`
);

export const selectDepartmentWagesByDate = createCachedSelector(
  (state: RootState, props: { departmentId: EntityId; date?: string }) =>
    selectRoleLaborTotalsByDepartmentByDate(state, props),
  roleLaborTotals =>
    roleLaborTotals.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.attributes.wages || 0),
      0
    )
)(
  (_, props) =>
    `selectDepartmentWagesByDate-${props.date}-${props.departmentId}`
);

export const selectAllDepartmentHoursByDate = createCachedSelector(
  (state: RootState, props: { date?: string }) => {
    const departmentIds = selectDepartmentIds(state);
    return departmentIds.flatMap(departmentId =>
      selectDepartmentHoursByDate(state, { ...props, departmentId })
    );
  },
  hours => hours
)((_, props) => `selectAllDepartmentHoursByDate-${props.date}`);

export const selectTotalHoursForAllDepartments = createCachedSelector(
  selectAllDepartmentHoursByDate,
  hours => hours.reduce((memo, hour) => memo + (hour || 0), 0)
)((_, props) => `selectTotalHoursForAllDepartments-${props.date}`);

export const selectAllDepartmentWagesByDate = createCachedSelector(
  (state: RootState, props: { date?: string }) => {
    const departmentIds = selectDepartmentIds(state);
    const wages = departmentIds.flatMap(departmentId =>
      selectDepartmentWagesByDate(state, { ...props, departmentId })
    );
    return wages;
  },
  wages => wages
)((_, props) => `selectAllDepartmentWagesByDate-${props.date}`);

export const selectTotalWagesForAllDepartments = createCachedSelector(
  selectAllDepartmentWagesByDate,
  wages => {
    const total = wages.reduce((memo, wage) => memo + (wage || 0), 0);
    return total;
  }
)((_, props) => `selectTotalWagesForAllDepartments-${props.date}`);

export const selectDepartmentPercentageByDate = createCachedSelector(
  selectTotalHoursForAllDepartments,
  selectDepartmentHoursByDate,
  (totalDepartmentsHours, departmentsHours) => {
    const percentage = !departmentsHours
      ? 0
      : Number(((100 * departmentsHours) / totalDepartmentsHours).toFixed(1));
    return percentage % 1 === 0 ? percentage.toFixed() : percentage.toFixed(1);
  }
)(
  (_, props) =>
    `selectDepartmentPercentageByDate-${props.date}-${props.departmentId}`
);
